// Generic Permission Checker for TrackProfit RBAC
// Provides centralized permission checking for pages, actions, and navigation

import { usePermissions } from '../contexts/PermissionContext';

/**
 * Generic permission checker utility
 * @param {string|string[]} permissions - Single permission or array of permissions
 * @param {string} mode - 'any' (default) or 'all' - check if user has any or all permissions
 * @param {function} hasPermission - Permission checking function from usePermissions hook
 * @returns {boolean} - True if user has required permissions
 */
export const checkPermission = (permissions, mode = 'any', hasPermission) => {
  if (!permissions) return true; // No permission required
  
  // Safety check: ensure hasPermission is a function
  if (typeof hasPermission !== 'function') {
    return false;
  }
  
  if (Array.isArray(permissions)) {
    if (mode === 'all') {
      return permissions.every(permission => hasPermission(permission));
    } else {
      return permissions.some(permission => hasPermission(permission));
    }
  }
  
  return hasPermission(permissions);
};

/**
 * Page permission definitions
 * Each page has required permissions to access
 */
export const PAGE_PERMISSIONS = {
  // Dashboard and Reports
  dashboard: 'reports:view',
  reports: 'reports:view',
  pl: 'reports:view',
  
  // Orders and Billing
  orders: 'orders:view',
  billing: 'orders:create',
  
  // Expenses
  expenses: 'expenses:view',
  
  // Products
  products: 'products:view',
  
  // Customers
  customers: 'customers:view',
  
  // Categories
  categories: 'categories:view',
  
  // Money Accounts
  accounts: 'money_accounts:view',
  'business-accounts': 'business_accounts:view',
  
  // User Management
  users: 'users:manage',
  
  // Profile (always accessible)
  profile: null,
};

/**
 * Action permission definitions
 * Each action has required permissions
 */
export const ACTION_PERMISSIONS = {
  // Order actions
  'orders:create': 'orders:create',
  'orders:edit': 'orders:edit',
  'orders:delete': 'orders:delete',
  'orders:view': 'orders:view',
  
  // Expense actions
  'expenses:create': 'expenses:create',
  'expenses:edit': 'expenses:edit',
  'expenses:delete': 'expenses:delete',
  'expenses:view': 'expenses:view',
  
  // Product actions
  'products:create': 'products:create',
  'products:edit': 'products:edit',
  'products:delete': 'products:delete',
  'products:view': 'products:view',
  'products:manage': 'products:manage',
  
  // Customer actions
  'customers:create': 'customers:create',
  'customers:edit': 'customers:edit',
  'customers:delete': 'customers:delete',
  'customers:view': 'customers:view',
  'customers:manage': 'customers:manage',
  
  // Category actions
  'categories:create': 'categories:create',
  'categories:edit': 'categories:edit',
  'categories:delete': 'categories:delete',
  'categories:view': 'categories:view',
  
  // Money account actions
  'money_accounts:create': 'money_accounts:create',
  'money_accounts:edit': 'money_accounts:edit',
  'money_accounts:delete': 'money_accounts:delete',
  'money_accounts:view': 'money_accounts:view',
  
  // Business account actions
  'business_accounts:create': 'business_accounts:create',
  'business_accounts:edit': 'business_accounts:edit',
  'business_accounts:delete': 'business_accounts:delete',
  'business_accounts:view': 'business_accounts:view',
  
  // User management actions
  'users:create': 'users:manage',
  'users:edit': 'users:manage',
  'users:delete': 'users:manage',
  'users:view': 'users:manage',
  'users:manage': 'users:manage',
  
  // Report actions
  'reports:view': 'reports:view',
  'reports:export': 'reports:export',
  
  // Billing actions
  'billing:create': 'orders:create',
};

/**
 * Check if user can access a specific page
 * @param {string} pageKey - Page identifier
 * @param {function} hasPermission - Permission checking function from usePermissions hook
 * @returns {boolean} - True if user can access the page
 */
export const canAccessPage = (pageKey, hasPermission) => {
  const requiredPermission = PAGE_PERMISSIONS[pageKey];
  return checkPermission(requiredPermission, 'any', hasPermission);
};

/**
 * Check if user can perform a specific action
 * @param {string} actionKey - Action identifier
 * @param {function} hasPermission - Permission checking function from usePermissions hook
 * @returns {boolean} - True if user can perform the action
 */
export const canPerformAction = (actionKey, hasPermission) => {
  const requiredPermission = ACTION_PERMISSIONS[actionKey];
  return checkPermission(requiredPermission, 'any', hasPermission);
};

/**
 * Filter navigation items based on permissions
 * @param {Array} navItems - Array of navigation items
 * @param {function} hasPermission - Permission checking function from usePermissions hook
 * @returns {Array} - Filtered navigation items
 */
export const filterNavItems = (navItems, hasPermission) => {
  return navItems.filter(item => {
    if (!item.permission) return true; // No permission required
    return checkPermission(item.permission, 'any', hasPermission);
  });
};

/**
 * Get the best landing page for a user based on their permissions
 * @param {function} hasPermission - Permission checking function from usePermissions hook
 * @returns {string} - Page key for the best landing page
 */
export const getBestLandingPage = (hasPermission) => {
  // Priority order for landing pages
  const landingPagePriority = [
    { page: 'dashboard', permission: 'reports:view' },
    { page: 'fast-billing', permission: 'orders:create' },
    { page: 'income', permission: 'orders:view' },
    { page: 'expenses', permission: 'expenses:view' },
    { page: 'customers', permission: 'customers:view' },
    { page: 'product-management', permission: 'products:view' },
    { page: 'categories', permission: 'categories:view' },
    { page: 'accounts', permission: 'money_accounts:view' },
  ];
  
  for (const { page, permission } of landingPagePriority) {
    if (hasPermission(permission)) {
      return page;
    }
  }
  
  return 'fast-billing'; // Default fallback to fast-billing instead of profile
};

/**
 * Permission-based component wrapper
 * Hides component if user doesn't have required permission
 */
export const PermissionGate = ({ permission, children, fallback = null }) => {
  const { hasPermission } = usePermissions();
  const hasAccess = checkPermission(permission, 'any', hasPermission);
  
  if (!hasAccess) {
    return fallback;
  }
  
  return children;
};

/**
 * Permission-based action wrapper
 * Disables action if user doesn't have required permission
 */
export const PermissionAction = ({ permission, children, disabled = false, ...props }) => {
  const { hasPermission } = usePermissions();
  const hasAccess = checkPermission(permission, 'any', hasPermission);
  
  return children({
    disabled: disabled || !hasAccess,
    hasPermission: hasAccess,
    ...props
  });
};

export default {
  checkPermission,
  canAccessPage,
  canPerformAction,
  filterNavItems,
  getBestLandingPage,
  PermissionGate,
  PermissionAction,
  PAGE_PERMISSIONS,
  ACTION_PERMISSIONS
};
